import React from 'react';
import { Space } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';

/**
 * 庫存欄位變動顯示（原始值 → 目標值）
 * 用於 InventoryTransformation 的 New/Edit/Show 頁面
 */

// 確保值是可 render 的字串（dayjs object → format, object → JSON）
function toDisplayString(val) {
  if (val == null || val === '') return null;
  if (val?.$isDayjsObject) return val.format('YYYY-MM-DD');
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

// 單欄位 diff（batch、expiration_date、inventory_type 等）
export function DiffCell({ source, target }) {
  const s = toDisplayString(source);
  const t = toDisplayString(target);
  const changed = t != null && t !== s;
  return (
    <Space direction="vertical" size={0}>
      <span style={{ textDecoration: changed ? 'line-through' : 'none', color: changed ? '#999' : 'inherit' }}>
        {s || '-'}
      </span>
      {changed && (
        <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{t}</span>
      )}
    </Space>
  );
}

// Custom attributes diff（多 key-value 對比）
/**
 * @param {object} props
 * @param {Record<string, unknown> | null | undefined} [props.source]
 * @param {Record<string, unknown> | null | undefined} [props.target]
 * @param {string} [props.emptyLabel]
 * @param {Record<string, string>} [props.attributeLabelByName]
 */
export function CustomAttributesDiffCell({ source, target, emptyLabel = '-', attributeLabelByName }) {
  const s = source || {};
  const tgt = target || {};
  const labelFor = (key) => attributeLabelByName?.[key] ?? key;

  const allKeys = [...new Set([...Object.keys(s), ...Object.keys(tgt)])];

  // 收集有值的 key（排除全空的）
  const entries = allKeys.filter(key => {
    const ov = s[key];
    const tv = tgt[key];
    return (ov != null && ov !== '') || (tv != null && tv !== '');
  });

  if (entries.length === 0) return emptyLabel;

  // 沒有 target → 只顯示原始值（StockAdjustment 用）
  const hasTarget = Object.keys(tgt).length > 0;

  if (!hasTarget) {
    return entries.map(k => `${labelFor(k)}: ${toDisplayString(s[k]) || '-'}`).join(', ');
  }

  // 有 target → 顯示 diff
  const changes = entries.map(key => ({
    key,
    oldVal: toDisplayString(s[key]) || '',
    newVal: toDisplayString(tgt[key]) || '',
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {changes.map(({ key, oldVal, newVal }) => {
        const changed = newVal && newVal !== oldVal;
        return (
          <div key={key} style={{ fontSize: 12 }}>
            <span style={{ color: '#666', marginRight: 4 }}>{labelFor(key)}:</span>
            {changed ? (
              <>
                <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 4 }}>{oldVal || emptyLabel}</span>
                <ArrowRightOutlined style={{ fontSize: 10, color: '#999', marginRight: 4 }} />
                <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{newVal}</span>
              </>
            ) : (
              <span>{oldVal || emptyLabel}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
